import { Fragment, ReactNode, useEffect, useState } from 'react';
import { Box, Fade } from '@mui/material';

import KeyCap from '@components/KeyCap';

import { useTutorialKeyHint, useTutorialStepText } from '../TutorialProvider';
import { useBottomPanelStyles } from '../sharedStyles';
import { useStyles } from './styles';

/**
 * Splits `text` on the first whole-word match of `keyHint` and swaps that
 * match for an inline `KeyCap`, so a narration like "press T on your
 * keyboard" renders the key reference as a key visual instead of a letter.
 * Returns the text unchanged when there's no hint or no match.
 *
 * @param text - Narration text to render.
 * @param keyHint - Letter to replace with a `KeyCap`, or `null`.
 */
const renderStepText = (text: string, keyHint: string | null): ReactNode => {
  if (!keyHint) {
    return text;
  }

  const match = new RegExp(`\\b${keyHint}\\b`).exec(text);

  if (!match) {
    return text;
  }

  const before = text.slice(0, match.index);
  const after = text.slice(match.index + match[0].length);

  return (
    <Fragment>
      {before}
      <Box
        component="span"
        sx={{ display: 'inline-flex', verticalAlign: 'middle', mx: '4px' }}
      >
        <KeyCap label={keyHint} size={30} />
      </Box>
      {after}
    </Fragment>
  );
};

/**
 * Shows the current tutorial step's narration text in a glass panel at the
 * same screen position as the Editor Mode bottom bar. Appears as soon as the
 * step's narration audio starts and shows the full text at once — no
 * letter-by-letter reveal. A step's key-hint letter (e.g. "T") renders inline
 * as a `KeyCap` rather than plain text. Fades in when a step starts narrating
 * and fades out when it ends; renders nothing the rest of the time.
 */
const TutorialStepText = () => {
  const panelClasses = useBottomPanelStyles();
  const classes = useStyles();
  const stepText = useTutorialStepText();
  const keyHint = useTutorialKeyHint();

  // Held separately from stepText/keyHint so the fade-out transition still
  // has content to render after the provider clears them to null.
  const [displayedText, setDisplayedText] = useState<string | null>(null);
  const [displayedKeyHint, setDisplayedKeyHint] = useState<string | null>(null);

  useEffect(() => {
    if (stepText) {
      setDisplayedText(stepText);
      setDisplayedKeyHint(keyHint);
    }
  }, [stepText, keyHint]);

  return (
    <Fade in={!!stepText} timeout={300} unmountOnExit>
      <Box sx={panelClasses.root} data-testid="tutorial-step-text">
        <Box sx={panelClasses.panel}>
          <Box sx={classes.text}>
            {renderStepText(displayedText ?? '', displayedKeyHint)}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default TutorialStepText;
